// Resolves each stop's effective delivery window into the two
// representations the Route Operations tab needs:
//   - "HH:MM" browser-local — PinStopModal prefill + useRouteEdits sorting
//     (same shape the merged startTimeWindow/endTimeWindow had before)
//   - "Jul 2, 13:00" browser-local — StopRow's read-only window cells
//
// Source priority per side (unchanged from the pre-date-display chain):
//   1. supervisor PinStop edit (stop.startTimeWindow — "HH:MM" local; the
//      date is implied by the stop's stockingDate)
//   2. NextMV input window (stop.nextmvDeliveryWindowStart/End —
//      "YYYY-MM-DD HH:MM:SS" UTC, populated for runs created after the BE
//      deploy)
//   3. NextMV optimizer's calculated arrival stopgap
//      (stop.calculatedStartTime/End — same UTC shape)
import {
  parseUtcDateTime,
  combineLocalDateAndTime,
  formatLocalHHMM,
  format12hClock,
  formatWindowDateTime,
  addOneDay,
} from '../../../utils/datetime';

// One side of the window. `timeOnly` marks sides whose date is implied by
// stockingDate rather than carried in the data — only those are eligible
// for the midnight-crossing roll below. A time-only side with a malformed
// stockingDate degrades to the bare clock string (date: null).
function resolveSide(hhmm, utcValue, calcValue, stockingDate) {
  if (hhmm) {
    return {
      date: combineLocalDateAndTime(stockingDate, hhmm),
      hhmm,
      timeOnly: true,
    };
  }
  const date = parseUtcDateTime(utcValue) || parseUtcDateTime(calcValue);
  if (!date) return null;
  return { date, hhmm: formatLocalHHMM(date), timeOnly: false };
}

function sideOutputs(side) {
  if (!side) return { hhmm: '', display: '' };
  return {
    hhmm: side.hhmm,
    // Bare-clock fallback (malformed stockingDate) still renders 12-hour so
    // every user-facing window string matches the TimeField dropdown labels.
    display: side.date
      ? formatWindowDateTime(side.date)
      : format12hClock(side.hhmm),
  };
}

export function resolveStopWindow(stop) {
  const start = resolveSide(
    stop.startTimeWindow,
    stop.nextmvDeliveryWindowStart,
    stop.calculatedStartTime,
    stop.stockingDate,
  );
  let end = resolveSide(
    stop.endTimeWindow,
    stop.nextmvDeliveryWindowEnd,
    stop.calculatedEndTime,
    stop.stockingDate,
  );
  // The next-day roll applies only when BOTH sides' dates are implied by
  // stockingDate (pin-edit windows); any date-bearing side means the dates
  // are real and must be trusted. A time-only end at or before the start
  // means the window closes on the next calendar day (13:00 – 01:00; equal
  // times = a 24-hour window — matching PinStopModal's validation).
  if (
    start &&
    end &&
    start.timeOnly &&
    end.timeOnly &&
    start.date &&
    end.date &&
    end.date.getTime() <= start.date.getTime()
  ) {
    end = { ...end, date: addOneDay(end.date) };
  }
  const startOut = sideOutputs(start);
  const endOut = sideOutputs(end);
  return {
    startTimeWindow: startOut.hhmm,
    endTimeWindow: endOut.hhmm,
    startTimeWindowDisplay: startOut.display,
    endTimeWindowDisplay: endOut.display,
  };
}

// Display strings for a supervisor-anchored window. Anchors are "HH:MM"
// local with the date implied by the stop's stockingDate — the same rules
// resolveStopWindow applies to pin edits, so it does the work.
export function anchorWindowDisplay(stop, anchor) {
  return resolveStopWindow({
    stockingDate: stop.stockingDate,
    startTimeWindow: anchor.startTimeWindow,
    endTimeWindow: anchor.endTimeWindow,
    // no UTC fields — only the pin-edit layer applies to anchors
  });
}
